import { format } from "date-fns";
import { ChevronRight, Edit } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import bankingbuilding from "../../assets/banking-building.jpg";
import BankInfoTab from "../../components/AdminPortal/BankingPartnerDetails/BankInfoTab";
import DashboardTab from "../../components/AdminPortal/BankingPartnerDetails/DashboardTab";
import EditBankingPartnerModal from "../../components/AdminPortal/BankingPartnerDetails/EditBankingPartnerModal";
import InsurancesTab from "../../components/AdminPortal/BankingPartnerDetails/InsurancesTab";
import LoansTab from "../../components/AdminPortal/BankingPartnerDetails/LoansTab";
import TopNavbar from "../../components/AdminPortal/TopNavbar";
import { useAuth } from "../../context/AuthContext";
import { useSidebar } from "../../context/SidebarContext";
import { fetchApplicationsByBankId } from "../../redux/applicationsSlice";
import { fetchPartnerById } from "../../redux/partnerSlice";
import { notificationAPI } from "../../services/notificationAPI";

const BankRepresentativeSection = ({ title, data }) => (
  <div className="col-span-6">
    <div className="font-medium text-customWhite mb-3 md:mb-4 text-sm md:text-base">
      {title}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mt-2">
      <div>
        <div className="text-customWhite mb-1 text-xs md:text-sm">
          Bank Representative
        </div>
        <div className="font-medium text-sm md:text-base">
          {data?.name || "-"}
        </div>
      </div>
      <div>
        <div className="text-customWhite mb-1 text-xs md:text-sm">
          Designation
        </div>
        <div className="font-medium text-sm md:text-base">
          {data?.designation || "-"}
        </div>
      </div>
      <div>
        <div className="text-customWhite mb-1 text-xs md:text-sm">
          Phone Number
        </div>
        <div className="font-medium text-sm md:text-base">
          {data?.phone || "-"}
        </div>
      </div>
      <div>
        <div className="text-customWhite mb-1 text-xs md:text-sm">Email ID</div>
        <div className="font-medium text-sm md:text-base">
          {data?.email || "-"}
        </div>
      </div>
    </div>
  </div>
);

// Format date function
const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    return format(new Date(dateString), "MMM dd, yyyy");
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

export default function PartnerDetails() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);
  const { sidebarExpanded } = useSidebar();
  const { id } = useParams();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { user } = useAuth();
  const adminId = user?.id;
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("Dashboard");

  // Redux selectors
  const partnerData = useSelector((state) => state.partner.data);
  const loadingPartner = useSelector((state) => state.partner.loading);
  const errorPartner = useSelector((state) => state.partner.error);
  const applications = useSelector((state) => state.applications.data);
  const loadingApplications = useSelector(
    (state) => state.applications.loading
  );
  const errorApplications = useSelector((state) => state.applications.error);

  useEffect(() => {
    if (id) {
      dispatch(fetchPartnerById(id));
      if (activeTab === "Dashboard") {
        dispatch(fetchApplicationsByBankId(id));
      }
    }
  }, [id, dispatch, activeTab]);

  const handleUpdatePartner = (updatedPartner) => {
    // Optionally, you can dispatch an action to update the partner in Redux
    // For now, just refetch
    if (id) dispatch(fetchPartnerById(id));
  };

  const sendNotification = async (
    userId,
    note,
    notificationType,
    senderUserId
  ) => {
    try {
      const notification = await notificationAPI.sendNotification(
        userId,
        note,
        notificationType,
        senderUserId
      );
      toast.success(`Notification sent: ${note}`);
      console.log("Notification sent:", notification);
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Could not send notification.");
    }
  };

  if (loadingPartner || loadingApplications) {
    return (
      <div className="min-h-screen bg-bgBackground flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-[#FFCA20] border-t-transparent rounded-full animate-spin"></div>
          <div className="text-customWhite">Loading partner details...</div>
        </div>
      </div>
    );
  }
  if (errorPartner || errorApplications) {
    return (
      <div className="min-h-screen bg-bgBackground flex items-center justify-center">
        <div className="text-customWhite">
          {errorPartner || errorApplications}
        </div>
      </div>
    );
  }

  const getBankLogo = (bankName) => {
    const bankLogos = {
      "STATE BANK OF INDIA":
        "https://upload.wikimedia.org/wikipedia/en/5/58/State_Bank_of_India_logo.svg",
      "HDFC BANK":
        "https://upload.wikimedia.org/wikipedia/commons/c/cc/HDFC_Bank_Logo.svg",
      "ICICI BANK":
        "https://upload.wikimedia.org/wikipedia/commons/1/12/ICICI_Bank_Logo.svg",
      "AXIS BANK":
        "https://upload.wikimedia.org/wikipedia/commons/1/1a/Axis_Bank_logo.svg",
      "PUNJAB NATIONAL BANK":
        "https://upload.wikimedia.org/wikipedia/en/1/1f/Punjab_National_Bank_logo.png",
      // Add more banks as needed
    };

    return bankLogos[bankName?.toUpperCase()] || "default-bank-logo.png";
  };
  const exportToCSV = (partnerData, applications) => {
    // Prepare bank information
    const bankInfo = {
      "Bank Name": partnerData?.bankName || "-",
      "Bank Category": partnerData?.bankCategory || "-",
      "Partnership Date": partnerData?.createdAt
        ? formatDate(partnerData.createdAt)
        : "-",
      "IFSC Code": partnerData?.branchIFSCCode || "-",
      "Branch Name": partnerData?.branchName || "-",
      "Bank Address": partnerData?.bankAddress || "-",
    };

    // Prepare representatives information
    const representatives = {
      "Manager Name": partnerData?.representatives?.manager?.name || "-",
      "Manager Phone": partnerData?.representatives?.manager?.phone || "-",
      "Manager Email": partnerData?.representatives?.manager?.email || "-",
      "Assistant Manager Name":
        partnerData?.representatives?.assistantManager?.name || "-",
      "Assistant Manager Phone":
        partnerData?.representatives?.assistantManager?.phone || "-",
      "Assistant Manager Email":
        partnerData?.representatives?.assistantManager?.email || "-",
      "Executive Name": partnerData?.representatives?.executive?.name || "-",
      "Executive Phone": partnerData?.representatives?.executive?.phone || "-",
      "Executive Email": partnerData?.representatives?.executive?.email || "-",
      "Loan Sales Manager Name":
        partnerData?.representatives?.loanSalesManager?.name || "-",
      "Loan Sales Manager Phone":
        partnerData?.representatives?.loanSalesManager?.phone || "-",
      "Loan Sales Manager Email":
        partnerData?.representatives?.loanSalesManager?.email || "-",
    };

    // Combine all data
    const data = {
      ...bankInfo,
      ...representatives,
      "Total Allocated Loans": "6",
      "Active Applications": "07",
      "Average Processing Time": "4 Days",
      "Total Loan Value": "₹20,70,000",
    };

    // Convert to CSV
    const csvRows = [];
    const headers = Object.keys(data);
    csvRows.push(headers.join(","));

    const values = Object.values(data).map((value) =>
      typeof value === "string" ? `"${value.replace(/"/g, '""')}"` : value
    );
    csvRows.push(values.join(","));

    // Download CSV
    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `${partnerData?.bankName || "bank"}_details.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  return (
    <div
      className="min-h-screen bg-black text-white p-3 md:p-4 mt-12"
      style={{
        marginLeft:
          window.innerWidth < 768 ? "0" : sidebarExpanded ? "200px" : "60px",
        width:
          window.innerWidth < 768
            ? "100%"
            : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
      }}
    >
      <TopNavbar />
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
        <main className="w-full space-y-6">
          {/* Navigation Section */}
          <section className="navigation-section">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center space-x-2 text-sm">
                <button
                  onClick={() => navigate("/admin/banking-partner/")}
                  className="text-[#FFCA20] hover:underline"
                >
                  Banking Partner
                </button>
                <span className="text-white">
                  <ChevronRight className="text-white w-4 h-4" />
                </span>
                <span className="text-gray-400">Partner Details</span>
              </div>
            </div>
          </section>

          {/* Partner Information Section */}
          <section className="partner-info-section">
            <div className="bg-[#131313] rounded-lg p-4 mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                  <img
                    src={getBankLogo(partnerData?.bankName)}
                    alt={bankingbuilding}
                    className="w-full h-full rounded object-contain"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = bankingbuilding;
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-lg md:text-xl font-semibold truncate">
                        {partnerData?.bankName || "Bank Name"}
                      </h2>
                      <div className="text-xs md:text-sm text-gray-400">
                        {partnerData?.bankCategory || "Bank Category"}
                      </div>
                      <div className="mt-1 text-xs md:text-sm text-green-500">
                        Partnership since:{" "}
                        {partnerData?.createdAt
                          ? formatDate(partnerData.createdAt)
                          : "-"}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setIsEditModalOpen(true)}
                        className="bg-transparent border border-[#FFCA20] text-[#FFCA20] px-3 py-1 rounded flex items-center text-sm hover:bg-[#FFCA20] hover:text-black transition-colors"
                      >
                        <Edit size={14} className="mr-1" />
                        <span>Edit Profile</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Navigation Tabs Section */}
          <section className="tabs-section">
            <div className="flex space-x-4 mb-6 border-b border-gray-700 overflow-x-auto scrollbar-hide">
              {["Dashboard", "Loans", "Insurances", "Bank Information"].map(
                (tab) => (
                  <button
                    key={tab}
                    className={`px-4 py-2 font-semibold whitespace-nowrap text-sm ${
                      activeTab === tab
                        ? "border-b-2 border-[#FFCA20] text-[#FFCA20]"
                        : "text-gray-400 hover:text-white"
                    }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                )
              )}
            </div>
          </section>

          {/* Content Section */}
          <section className="content-section">
            <div className="bg-[#131313] rounded-lg p-4 md:p-6 min-h-[400px]">
              {activeTab === "Dashboard" && (
                <DashboardTab partnerData={partnerData} />
              )}
              {activeTab === "Loans" && <LoansTab partnerData={partnerData} />}
              {activeTab === "Insurances" && (
                <InsurancesTab partnerData={partnerData} />
              )}
              {activeTab === "Bank Information" && (
                <BankInfoTab
                  partnerData={partnerData}
                  sendNotification={sendNotification}
                />
              )}
            </div>
          </section>

          {/* Modal Section */}
          <EditBankingPartnerModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            partner={partnerData}
            onUpdate={handleUpdatePartner}
          />
        </main>
      </div>
      <ToastContainer />
    </div>
  );
}
